import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scissors, ReceiptText, Percent, TrendingDown, PlayCircle, Heart, HeartHandshake,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { useAuth } from "@/context/AuthContext";
import KpiCard from "@/components/KpiCard";
import DailyHealthBar from "@/components/DailyHealthBar";
import VideoEvidenceModal from "@/components/VideoEvidenceModal";
import SeatHistoryModal from "@/components/SeatHistoryModal";

function fmtTime(iso) {
  return new Date(iso).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" });
}

export default function Dashboard() {
  const { money, branding } = useBranding();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [seat, setSeat] = useState(null);

  const { data: summary } = useQuery({
    queryKey: ["summary"],
    queryFn: () => api.get("/dashboard/summary").then((r) => r.data),
  });
  const { data: flagged } = useQuery({
    queryKey: ["flagged-events"],
    queryFn: () => api.get("/events", { params: { flagged: true, limit: 6 } }).then((r) => r.data),
  });
  const { data: giving } = useQuery({
    queryKey: ["giving-summary"],
    queryFn: () => api.get("/giving/summary").then((r) => r.data),
  });

  const openSeat = (ev) => setSeat({ storeId: ev.store_id, chair: { id: ev.chair_id, label: ev.chair_label } });
  const openStore = (ev, e) => { e.stopPropagation(); navigate(`/floor?store=${ev.store_number}&from=dashboard`); };

  const c = summary?.classification || { quick: 0, standard: 0, extended: 0 };
  const pieData = [
    { name: "Quick", value: c.quick, color: "#3B82F6" },
    { name: "Standard", value: c.standard, color: "#06B6D4" },
    { name: "Extended", value: c.extended, color: "#EC4899" },
  ];

  return (
    <div>
      <div className="surface-card relative overflow-hidden px-6 md:px-8 py-8" style={{ background: "linear-gradient(115deg,#243049 0%,#1E2638 55%)" }}>
        <img
          src="https://images.unsplash.com/photo-1621645582931-d1d3e6564943"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-6 top-1/2 -translate-y-1/2 h-[150%] w-1/2 object-cover opacity-[0.12]"
          style={{ maskImage: "linear-gradient(to left, black, transparent)", WebkitMaskImage: "linear-gradient(to left, black, transparent)" }}
        />
        <div className="relative flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Revenue Assurance</div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
              Here's what your seats are telling you
            </h1>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Reconciled this month</div>
            <div className="font-display text-3xl md:text-4xl font-light" style={{ color: "#10B981" }} data-testid="reconciled-month">
              {summary ? money(summary.reconciled_month) : "—"}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {summary?.store_count} {summary?.store_count === 1 ? "store" : "stores"} · live
            </div>
          </div>
        </div>
      </div>

      <DailyHealthBar />

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard testid="kpi-services" index={0} label="Services Delivered" value={summary?.services_delivered ?? "—"}
          sub="Every service on the ledger →" icon={Scissors}
          onClick={() => navigate("/reconciliation")} />
        <KpiCard testid="kpi-sales" index={1} label="Sales Recorded" value={summary?.sales_recorded ?? "—"}
          sub="See the matched sales →" icon={ReceiptText}
          onClick={() => navigate("/reconciliation?filter=matched")} />
        <KpiCard testid="kpi-match-rate" index={2} label="Match Rate" value={summary ? `${summary.match_rate}%` : "—"}
          sub="Matched vs unmatched →" accent={branding.accent_color} icon={Percent}
          onClick={() => navigate("/reconciliation")} />
        <KpiCard testid="kpi-unaccounted" index={3} label="Unaccounted Revenue" value={summary ? money(summary.unaccounted_revenue) : "—"}
          sub={`${summary?.flagged_events ?? 0} flagged services →`} accent={branding.primary_color} icon={TrendingDown}
          onClick={() => navigate("/reconciliation?filter=flagged")} />
      </div>

      {/* Community Giving */}
      {giving && giving.total_count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="mt-6 surface-card p-6" data-testid="community-giving-card"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center border border-[#2A3550]" style={{ background: "#2A2036" }}>
                <HeartHandshake className="h-5 w-5 text-rose-400" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Community Giving</div>
                <div className="font-display text-base font-medium">Charitable cuts credited to your causes</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-light text-rose-300" data-testid="giving-total">{money(giving.total_donated)}</div>
              <div className="text-xs text-slate-400">{giving.total_count} free cut{giving.total_count !== 1 ? "s" : ""} donated</div>
            </div>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {giving.beneficiaries.slice(0, 6).map((b) => (
              <div key={b.beneficiary} className="rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3" data-testid="giving-beneficiary-row">
                <div className="text-sm font-medium truncate flex items-center gap-2"><Heart className="h-3.5 w-3.5 text-rose-400 shrink-0" />{b.beneficiary}</div>
                <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
                  <span>{b.count} cut{b.count !== 1 ? "s" : ""}</span>
                  <span className="text-rose-300 font-medium">{money(b.donated)}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Flagged feed */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="lg:col-span-2 surface-card p-6"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-medium">Needs your attention</h2>
            <span className="text-xs text-slate-400">Click a row for seat history · store # for the floor</span>
          </div>
          <div className="mt-5 space-y-3">
            {(flagged || []).map((ev) => (
              <div
                key={ev.id}
                onClick={() => openSeat(ev)}
                role="button" tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openSeat(ev)}
                data-testid={`flagged-event-${ev.id}`}
                title="View this seat's history"
                className="w-full text-left flex items-center justify-between gap-4 rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3.5 hover:border-red-500/40 transition-colors duration-200 group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-500/100 pulse-status shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      <button
                        onClick={(e) => openStore(ev, e)}
                        data-testid={`flagged-store-${ev.id}`}
                        title="Open this store's live floor"
                        className="hover:text-[#38BDF8] transition-colors duration-150"
                      >
                        Store #{ev.store_number}
                      </button>
                      {" "}— {ev.chair_label} — no sale recorded
                    </div>
                    <div className="text-xs text-slate-400">
                      {fmtTime(ev.started_at)} · {ev.duration_min} min · {ev.classification}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-display text-lg" style={{ color: branding.primary_color }}>
                    {money(ev.expected_price)}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelected(ev); }}
                    data-testid={`flagged-watch-${ev.id}`}
                    title="Open Video Protocol"
                    className="inline-flex items-center justify-center rounded-lg p-1.5 text-slate-400 hover:text-white hover:bg-[#232C40] transition-colors duration-200"
                  >
                    <PlayCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
            {flagged && flagged.length === 0 && (
              <div className="text-sm text-slate-400 py-8 text-center">No revenue flags. Every service matched a sale.</div>
            )}
          </div>
        </motion.div>

        {/* Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="surface-card p-6"
        >
          <h2 className="font-display text-lg font-medium">Service mix</h2>
          <p className="text-xs text-slate-400 mt-1">Classified by seated duration</p>
          <div className="h-44 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={3} stroke="none">
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#171E2E", border: "1px solid #2A3550", borderRadius: 8, fontSize: 12 }}
                  itemStyle={{ color: "#E2E8F0" }} labelStyle={{ color: "#E2E8F0" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-2">
            {pieData.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color }} /> {p.name}
                </div>
                <span className="text-slate-400">{p.value}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <VideoEvidenceModal event={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} />
      <SeatHistoryModal storeId={seat?.storeId} chair={seat?.chair} open={!!seat} onOpenChange={(v) => !v && setSeat(null)} />
    </div>
  );
}
